import {
  HubConnectionBuilder,
  HttpTransportType,
  LogLevel,
} from '@microsoft/signalr';
import type { HumanConversationNotifications } from './direct-call-events';
import type { HttpClient } from '@microsoft/signalr';
import { HumanConnectionBinding } from './direct-call-binding';
import type { HumanConnectionProofApi } from './direct-call-binding';

/** Owns a private human-session hub; message payloads only wake durable replay. */
export class HumanCallNotifications implements HumanConversationNotifications {
  private readonly lifetime = new AbortController();
  private readonly hub;
  private closed = false;
  private subscribed = false;
  private proof?: HumanConnectionBinding;
  private proofOperation?: AbortController;
  private boundId: string | null = null;
  private renewalTimer: ReturnType<typeof setTimeout> | undefined;
  private speechSubscriptions = 0;
  private pendingRenewal?: () => void;
  private readonly readyListeners = new Set<() => void>();

  get bindingId(): string | null {
    return this.boundId;
  }

  waitForBinding(signal: AbortSignal): Promise<string> {
    return new Promise((resolve, reject) => {
      const cleanup = () => {
        clearTimeout(timer);
        this.readyListeners.delete(check);
        signal.removeEventListener('abort', check);
      };
      const check = () => {
        if (this.closed || signal.aborted) {
          cleanup();
          reject(new Error('Call closed before audio binding'));
        } else if (this.boundId) {
          cleanup();
          resolve(this.boundId);
        }
      };
      const timer = setTimeout(() => {
        cleanup();
        reject(new Error('Audio binding timed out'));
      }, 15000);
      this.readyListeners.add(check);
      signal.addEventListener('abort', check, { once: true });
      check();
    });
  }

  /** US 36579: a raw hub event (incomingCall, callInviteEnded). Returns its unsubscribe. */
  on(type: string, handler: (value: unknown) => void): () => void {
    const wrapped = (value: unknown) => {
      if (!this.closed) handler(value);
    };
    this.hub.on(type, wrapped);
    return () => this.hub.off(type, wrapped);
  }

  subscribeSpeech(receive: (type: string, value: unknown) => void): () => void {
    if (this.closed) throw new Error('Call is closed');
    this.speechSubscriptions++;
    const handlers = [
      'speechChunk',
      'speechEnd',
      'speechError',
      'speechAbort',
    ].map((type) => {
      const handler = (value: unknown) => {
        if (!this.closed) receive(type, value);
      };
      this.hub.on(type, handler);
      return { type, handler };
    });
    let released = false;
    return () => {
      if (released) return;
      released = true;
      this.speechSubscriptions--;
      for (const { type, handler } of handlers) this.hub.off(type, handler);
      if (this.speechSubscriptions === 0) this.pendingRenewal?.();
    };
  }

  constructor(
    baseUrl: string,
    humanToken: (signal: AbortSignal) => Promise<string>,
    private readonly proofApi: HumanConnectionProofApi,
    httpClient?: HttpClient,
    webSocket?: typeof WebSocket
  ) {
    const url = new URL(baseUrl);
    if (
      url.protocol !== 'https:' ||
      url.username ||
      url.password ||
      url.search ||
      url.hash
    )
      throw new Error('An HTTPS bridge API URL is required');
    url.searchParams.set('audience', 'human-session');
    this.hub = new HubConnectionBuilder()
      .withUrl(url.href, {
        transport: HttpTransportType.WebSockets,
        withCredentials: false,
        httpClient,
        // SignalR runtime supports this platform override; the package contract
        // test pins it because it is omitted from the public option typings.
        ...(webSocket ? { WebSocket: webSocket } : {}),
        timeout: 15000,
        accessTokenFactory: async () => {
          if (this.closed) throw new Error('Call is closed');
          const token = await humanToken(this.lifetime.signal);
          if (this.closed || !token?.trim())
            throw new Error('Human sign-in required');
          return token;
        },
      })
      .configureLogging(LogLevel.None)
      .withAutomaticReconnect([0, 1000, 3000, 5000])
      .build();
  }

  subscribe(
    changed: (id: string) => void,
    reconnected: () => void,
    failed: () => void
  ): () => void {
    if (this.closed || this.subscribed)
      throw new Error('Notifications are unavailable');
    this.subscribed = true;
    const message = (value: unknown) => {
      if (this.closed || !value || typeof value !== 'object') return;
      const id = (value as { conversationId?: unknown }).conversationId;
      if (typeof id === 'string' && id) changed(id);
    };
    this.hub.on('newMessage', message);
    this.hub.on('connectionChallenge', (value) => this.proof?.receive(value));
    const bind = async () => {
      if (this.closed) return;
      this.clearBinding();
      const operation = new AbortController();
      this.proofOperation = operation;
      const proof = new HumanConnectionBinding(this.proofApi);
      this.proof = proof;
      try {
        const id = await proof.bind(
          this.hub.connectionId ?? '',
          operation.signal
        );
        if (this.closed || operation.signal.aborted || this.proof !== proof)
          return;
        this.boundId = id;
        // Proofs expire after ten minutes; allow the bounded current stream to
        // finish on its original proof before replacing the binding.
        const renew = () => {
          if (this.closed || this.proof !== proof) return;
          if (this.speechSubscriptions > 0) {
            this.pendingRenewal = renew;
            this.renewalTimer = setTimeout(renew, 5000);
          } else void bind();
        };
        this.renewalTimer = setTimeout(renew, 5 * 60 * 1000);
        for (const listener of this.readyListeners) listener();
        reconnected();
      } catch {
        if (!this.closed && !operation.signal.aborted && this.proof === proof)
          failed();
      }
    };
    this.hub.onreconnecting(() => this.clearBinding());
    this.hub.onreconnected(() => {
      void bind();
    });
    this.hub.onclose(() => {
      this.clearBinding();
      if (!this.closed) failed();
    });
    // Initial connection failure is terminal, not a hidden infinite retry loop.
    void this.hub
      .start()
      .then(() => {
        if (this.closed) return this.hub.stop();
        return bind();
      })
      .catch(() => {
        if (!this.closed) failed();
      });
    return () => this.close();
  }

  close(): void {
    if (this.closed) return;
    this.closed = true;
    for (const listener of this.readyListeners) listener();
    this.lifetime.abort();
    this.clearBinding();
    this.hub.off('newMessage');
    this.hub.off('connectionChallenge');
    void this.hub.stop().catch(() => undefined);
  }

  private clearBinding(): void {
    clearTimeout(this.renewalTimer);
    this.renewalTimer = undefined;
    this.pendingRenewal = undefined;
    this.boundId = null;
    this.proofOperation?.abort();
    this.proofOperation = undefined;
    this.proof = undefined;
  }
}
